import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TimeEntryFilters = ({ 
  onFiltersChange, 
  onReset,
  totalEntries = 0,
  filteredEntries = 0,
  employees = []
}) => {
  const [filters, setFilters] = useState({
    dateRange: {
      startDate: '',
      endDate: ''
    },
    employee: '',
    hireType: '',
    entryType: '',
    status: '',
    searchQuery: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Helper function to get current month date range
  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format dates as YYYY-MM-DD using local timezone
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(firstDay),
      endDate: formatDate(lastDay)
    };
  };

  // Set default filter to current month on component mount
  useEffect(() => {
    const currentMonthRange = getCurrentMonthRange();
    const defaultFilters = {
      dateRange: currentMonthRange,
      employee: '',
      hireType: '',
      entryType: '',
      status: '',
      searchQuery: ''
    };
    setFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  }, []); // Empty dependency array means this runs once on mount

  // Generate hire type options from real employee data
  const hireTypeOptions = [
    { value: '', label: 'Alle Anstellungsarten' },
    { value: 'Vollzeit', label: 'Vollzeit' },
    { value: 'Teilzeit', label: 'Teilzeit' },
    { value: 'Mini-job', label: 'Mini-Job' }
  ];

  const entryTypeOptions = [
    { value: '', label: 'Alle Eintragstypen' },
    { value: 'kiosk', label: 'Kiosk-Eintrag' },
    { value: 'manual', label: 'Manueller Eintrag' },
    { value: 'qr', label: 'QR-Code-Eintrag' },
    { value: 'mobile', label: 'Mobiler Eintrag' }
  ];


  // Generate employee options from real employee data
  const employeeOptions = [
    { value: '', label: 'Alle Mitarbeiter' },
    ...employees.map(emp => ({ 
      value: emp.id, 
      label: `${emp.name} (${emp.employee_code})` 
    }))
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (field, value) => {
    const newDateRange = { ...filters?.dateRange, [field]: value };
    const newFilters = { ...filters, dateRange: newDateRange };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      dateRange: { startDate: '', endDate: '' },
      employee: '',
      hireType: '',
      entryType: '',
      status: '',
      searchQuery: ''
    };
    setFilters(resetFilters);
    onReset();
  };

  // Helper function to get last month date range
  const getLastMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    
    // Format dates as YYYY-MM-DD using local timezone
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(firstDay),
      endDate: formatDate(lastDay)
    };
  };

  // Handle quick month filters
  const handleCurrentMonthFilter = () => {
    const monthRange = getCurrentMonthRange();
    const newFilters = {
      ...filters,
      dateRange: monthRange
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleLastMonthFilter = () => {
    const monthRange = getLastMonthRange();
    const newFilters = {
      ...filters,
      dateRange: monthRange
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleTodayFilter = () => {
    const today = new Date();
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const todayStr = formatDate(today);
    const newFilters = {
      ...filters,
      dateRange: {
        startDate: todayStr,
        endDate: todayStr
      }
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleShowAllFilter = () => {
    const newFilters = {
      ...filters,
      dateRange: {
        startDate: '',
        endDate: ''
      }
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const hasActiveFilters = () => {
    return filters?.searchQuery || 
           filters?.employee || 
           filters?.hireType || 
           filters?.entryType || 
           filters?.status ||
           filters?.dateRange?.startDate ||
           filters?.dateRange?.endDate;
  };

  // Do not set a restrictive default date range; keep empty so search works globally

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Zeiteinträge filtern</h3>
          {hasActiveFilters() && (
            <div className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs font-medium">
              {filteredEntries} von {totalEntries} Einträgen
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              iconName="X"
              iconPosition="left"
              className="text-text-secondary hover:text-text-primary"
            >
              Filter zurücksetzen
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            className="text-text-secondary hover:text-text-primary"
          >
            {isExpanded ? 'Weniger Filter' : 'Mehr Filter'}
          </Button>
        </div>
      </div>
      {/* Quick Search */}
      <div className="mb-4">
        <Input
          type="search"
          placeholder="Suche nach Mitarbeitername, ID oder Notizen..."
          value={filters?.searchQuery}
          onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Date Range - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Input
          type="date"
          label="Startdatum"
          value={filters?.dateRange?.startDate}
          onChange={(e) => handleDateRangeChange('startDate', e?.target?.value)}
        />
        <Input
          type="date"
          label="Enddatum"
          value={filters?.dateRange?.endDate}
          onChange={(e) => handleDateRangeChange('endDate', e?.target?.value)}
        />
      </div>

      {/* Quick Month Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="text-sm text-text-secondary font-medium">Schnellfilter:</span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleShowAllFilter}
          className="text-muted-foreground hover:bg-orange-300 hover:text-white"
        >
          <Icon name="List" size={14} className="mr-1" />
          Alle anzeigen
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTodayFilter}
          className="text-accent hover:bg-orange-300 hover:text-white"
        >
          <Icon name="Calendar" size={14} className="mr-1" />
          Nur Heute
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCurrentMonthFilter}
          className="text-primary hover:bg-orange-300 hover:text-white"
        >
          <Icon name="Calendar" size={14} className="mr-1" />
          Aktueller Monat
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleLastMonthFilter}
          className="text-secondary hover:bg-orange-300 hover:text-white"
        >
          <Icon name="Calendar" size={14} className="mr-1" />
          Letzter Monat
        </Button>
      </div>
      {/* Expanded Filters */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border">
          <Select
            label="Mitarbeiter"
            options={employeeOptions}
            value={filters?.employee}
            onChange={(value) => handleFilterChange('employee', value)}
            searchable
            clearable
          />

          <Select
            label="Anstellungsart"
            options={hireTypeOptions}
            value={filters?.hireType}
            onChange={(value) => handleFilterChange('hireType', value)}
          />
{/* <Select
            label="Eintragstyp"
            options={entryTypeOptions}
            value={filters?.entryType}
            onChange={(value) => handleFilterChange('entryType', value)}
          />

          */}
        </div>
      )}
      {/* Quick Filter Buttons */}
      {/* <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
        <span className="text-sm text-text-secondary font-medium">Schnellfilter:</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFilterChange('status', 'pending')}
          className="text-warning hover:bg-warning/10"
        >
          Ausstehende Genehmigung
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFilterChange('status', 'flagged')}
          className="text-error hover:bg-error/10"
        >
          Markierte Einträge
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleFilterChange('entryType', 'manual')}
          className="text-accent hover:bg-accent/10"
        >
          Manuelle Einträge
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const today = new Date()?.toISOString()?.split('T')?.[0];
            handleDateRangeChange('startDate', today);
            handleDateRangeChange('endDate', today);
          }}
          className="text-primary hover:bg-primary/10"
        >
          Nur Heute
        </Button>
      </div> */}
    </div>
  );
};

export default TimeEntryFilters;